/**
 * Toast Component - Meridian Dark Design System
 *
 * A notification component with variants, auto-dismiss, and swipe gestures.
 * Built on Radix UI Toast for accessibility and animation support.
 *
 * @example
 * const { toast } = useToast();
 *
 * toast({
 *   title: "Success",
 *   description: "Your changes have been saved.",
 *   variant: "success",
 *   duration: 5000,
 * });
 */

import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROL_HEIGHT } from '@/components/ui/control-size';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-right' | 'bottom-right' | 'top-center' | 'bottom-center';

const ToastProvider = ToastPrimitive.Provider;

/**
 * Toast viewport container with position support
 */
const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport> & {
    position?: ToastPosition;
  }
>(({ className, position = 'top-right', ...props }, ref) => {
  const positionClasses: Record<ToastPosition, string> = {
    'top-right': 'top-0 right-0',
    // bottom-chat-safe clears both the page's own action buttons and the
    // Bitrix24 chat button in the corner (styles/chat-widget.css). bottom-24
    // used to leave only 4px of headroom above it.
    'bottom-right': 'bottom-chat-safe right-0',
    'top-center': 'top-0 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-chat-safe left-1/2 -translate-x-1/2',
  };

  const edge = position.startsWith('top') ? 'top' : 'bottom';

  return (
    <ToastPrimitive.Viewport
      ref={ref}
      // In a content-sized embedded frame the viewport sits at this edge of the part the portal shows (index.css).
      data-frame-edge={edge}
      className={cn(
        // z-toast (120), not z-modal (100): a toast fired from inside a Sheet or
        // Dialog must sit ABOVE it. At equal z-index the later portal — the
        // overlay — wins on paint order, which silently swallowed every error
        // toast raised from a slide-over (the save failed, the sheet stayed
        // open, and nothing explained why).
        'fixed z-toast flex max-h-screen w-full flex-col-reverse p-4 sm:max-w-[420px]',
        positionClasses[position],
        position.includes('bottom') && 'flex-col',
        // The chat-widget clearance survives the re-anchoring: index.css adds it to the visible bottom.
        position.includes('bottom') && '[--frame-edge-inset:var(--spacing-chat-safe)]',
        className
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = {
  success: {
    className: 'border-border',
    style: {
      backgroundColor: 'color-mix(in srgb, var(--color-success) 65%, transparent)',
      borderColor: 'color-mix(in srgb, var(--color-success) 70%, transparent)',
    },
    icon: CheckCircle2,
    iconClass: 'text-success',
  },
  error: {
    className: 'border-danger',
    style: {
      backgroundColor: 'color-mix(in srgb, var(--color-danger) 65%, transparent)',
      borderColor: 'color-mix(in srgb, var(--color-danger) 70%, transparent)',
    },
    icon: XCircle,
    iconClass: 'text-danger',
  },
  warning: {
    className: 'border-border',
    style: {
      backgroundColor: 'color-mix(in srgb, var(--color-warning) 65%, transparent)',
      borderColor: 'color-mix(in srgb, var(--color-warning) 70%, transparent)',
    },
    icon: AlertTriangle,
    iconClass: 'text-warning',
  },
  info: {
    className: 'border-border',
    style: {
      backgroundColor: 'color-mix(in srgb, var(--color-info) 65%, transparent)',
      borderColor: 'color-mix(in srgb, var(--color-info) 70%, transparent)',
    },
    icon: Info,
    iconClass: 'text-info',
  },
};

export interface ToastProps extends Omit<
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
  'open'
> {
  variant?: ToastVariant;
  title?: string;
  description?: React.ReactNode;
  action?: React.ReactElement;
  onClose?: () => void;
  duration?: number;
}

/**
 * Toast component with variants and animations
 */
const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  (
    {
      className,
      variant = 'info',
      title,
      description,
      action,
      onClose,
      duration,
      children,
      ...props
    },
    ref
  ) => {
    const variantConfig = toastVariants[variant];
    const Icon = variantConfig.icon;

    return (
      <ToastPrimitive.Root
        ref={ref}
        // A toast on screen keeps the frame reading fresh, so it follows the portal as it scrolls
        // (lib/embedded-frame-viewport.ts); the always-mounted viewport must NOT carry this.
        data-frame-follow=""
        defaultOpen={true}
        duration={duration}
        onOpenChange={(open) => {
          if (!open && onClose) onClose();
        }}
        className={cn(
          // Base styles
          'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
          'border rounded-card shadow-lg p-4',
          // Variant styles (border color)
          variantConfig.className,
          // Entrance animation - slide in from right with fade
          'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full data-[state=open]:fade-in-0',
          // Exit animation
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-80',
          'data-[state=closed]:slide-out-to-right-full',
          // Swipe gestures
          'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
          'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
          'data-[swipe=move]:transition-none',
          'transition-all duration-200',
          className
        )}
        style={{
          ...variantConfig.style,
          ...props.style,
        }}
        {...props}
      >
        {/* Icon */}
        <Icon className={cn('h-5 w-5 shrink-0', variantConfig.iconClass)} />

        {/* Content */}
        <div className="flex-1 grid gap-1">
          {title && (
            <ToastPrimitive.Title className="text-sm font-semibold text-fg-primary">
              {title}
            </ToastPrimitive.Title>
          )}
          {description && (
            <ToastPrimitive.Description className="text-sm text-fg-secondary">
              {description}
            </ToastPrimitive.Description>
          )}
          {children}
        </div>

        {/* Action */}
        {action}

        {/* Close button */}
        <ToastPrimitive.Close
          className={cn(
            'absolute right-2 top-2 rounded-sm p-1',
            'text-fg-subtle hover:text-fg hover:bg-bg-subtle',
            'opacity-0 group-hover:opacity-100',
            'transition-all duration-150',
            'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/30'
          )}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </ToastPrimitive.Close>
      </ToastPrimitive.Root>
    );
  }
);
Toast.displayName = ToastPrimitive.Root.displayName;

/**
 * Toast action button
 */
const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      CONTROL_HEIGHT.sm,
      'inline-flex shrink-0 items-center justify-center rounded-button border',
      'border-border bg-transparent px-3 text-sm font-medium',
      'transition-all duration-150 hover:bg-bg-subtle',
      'focus:outline-none focus:ring-2 focus:ring-primary/30',
      'disabled:pointer-events-none disabled:opacity-50',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

/**
 * Toaster component - renders all toasts
 */
const Toaster = ({ position = 'top-right' }: { position?: ToastPosition }) => {
  return (
    <ToastProvider>
      <ToastViewport position={position} />
    </ToastProvider>
  );
};

export { ToastProvider, ToastViewport, Toast, ToastAction, Toaster };
